import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Skeleton,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  Plus,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Trash2,
  ArrowRightLeft
} from 'lucide-react';
import {
  getCashBankAccounts,
  getTransactions,
  addManualTransaction,
  transferFunds,
  reconcileAccount,
  getCashFlowSummary,
  deleteTransaction,
  initializeCashBankAccounts
} from '../firebase/cashBankService';

const CashBankLedger = ({ currentUser, permissions }) => {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cashFlowSummary, setCashFlowSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Dialogs
  const [transactionDialog, setTransactionDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [reconcileDialog, setReconcileDialog] = useState(false);
  const [initDialog, setInitDialog] = useState(false);
  
  // Transaction form
  const [transactionForm, setTransactionForm] = useState({
    accountType: 'cash_in_hand',
    transactionType: 'credit',
    amount: '',
    description: '',
    paymentMethod: 'cash',
    reference: '',
    notes: ''
  });
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    fromAccount: 'cash_in_hand',
    toAccount: 'cash_at_bank',
    amount: '',
    notes: ''
  });
  
  // Reconciliation form
  const [reconcileForm, setReconcileForm] = useState({
    accountType: 'cash_in_hand',
    date: new Date().toISOString().split('T')[0],
    systemBalance: 0,
    physicalCount: '',
    difference: 0,
    notes: ''
  });
  
  // Initialization form
  const [initForm, setInitForm] = useState({
    cashInHand: '',
    pettyCash: '',
    cashAtBank: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAccount !== 'all') {
      loadTransactions(selectedAccount);
    } else {
      loadTransactions();
    }
  }, [selectedAccount]);

  const loadData = async () => {
    setLoading(true);
    
    // Load accounts
    const accountsResult = await getCashBankAccounts();
    if (accountsResult.success) {
      setAccounts(accountsResult.data);
      
      // Check if accounts are initialized
      if (accountsResult.data.length === 0) {
        setInitDialog(true);
      }
    }
    
    // Load transactions
    await loadTransactions();
    
    // Load cash flow summary
    const summaryResult = await getCashFlowSummary();
    if (summaryResult.success) {
      setCashFlowSummary(summaryResult.data);
    }
    
    setLoading(false);
  };

  const loadTransactions = async (accountType = null) => {
    const filters = accountType ? { accountType } : {};
    const result = await getTransactions(filters);
    if (result.success) {
      setTransactions(result.data);
    }
  };

  const handleAddTransaction = async () => {
    if (submitting) return;
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }
    setSubmitting(true);
    const result = await addManualTransaction(transactionForm, currentUser);
    setSubmitting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'Transaction added successfully', severity: 'success' });
      setTransactionDialog(false);
      resetTransactionForm();
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleTransfer = async () => {
    if (submitting) return;
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }
    setSubmitting(true);
    const result = await transferFunds(transferForm.fromAccount, transferForm.toAccount, transferForm.amount, transferForm.notes, currentUser);
    setSubmitting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'Transfer completed successfully', severity: 'success' });
      setTransferDialog(false);
      resetTransferForm();
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleReconcile = async () => {
    if (submitting) return;
    if (!reconcileForm.physicalCount) {
      setSnackbar({ open: true, message: 'Please enter physical count', severity: 'error' });
      return;
    }
    setSubmitting(true);
    const result = await reconcileAccount(reconcileForm, currentUser);
    setSubmitting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'Account reconciled successfully', severity: 'success' });
      setReconcileDialog(false);
      resetReconcileForm();
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleInitialize = async () => {
    if (submitting) return;
    if (!initForm.cashInHand && !initForm.pettyCash && !initForm.cashAtBank) {
      setSnackbar({ open: true, message: 'Please enter at least one opening balance', severity: 'error' });
      return;
    }
    setSubmitting(true);
    const openingBalances = { cashInHand: parseFloat(initForm.cashInHand) || 0, pettyCash: parseFloat(initForm.pettyCash) || 0, cashAtBank: parseFloat(initForm.cashAtBank) || 0 };
    const result = await initializeCashBankAccounts(openingBalances, currentUser);
    setSubmitting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'Accounts initialized successfully', severity: 'success' });
      setInitDialog(false);
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDeleteTransaction = async (transaction) => {
    setDeleteConfirm(transaction);
  };

  const confirmDelete = async () => {
    const transaction = deleteConfirm;
    setDeleteConfirm(null);
    if (!transaction) return;

    const result = await deleteTransaction(transaction.id, currentUser);
    
    if (result.success) {
      setSnackbar({ 
        open: true, 
        message: 'Transaction deleted successfully', 
        severity: 'success' 
      });
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleOpenReconcileDialog = (account) => {
    setReconcileForm({
      accountType: account.accountType,
      date: new Date().toISOString().split('T')[0],
      systemBalance: account.currentBalance,
      physicalCount: '',
      difference: 0,
      notes: ''
    });
    setReconcileDialog(true);
  };

  const calculateDifference = (physicalCount) => {
    const physical = parseFloat(physicalCount) || 0;
    const system = parseFloat(reconcileForm.systemBalance) || 0;
    return physical - system;
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      accountType: 'cash_in_hand',
      transactionType: 'credit',
      amount: '',
      description: '',
      paymentMethod: 'cash',
      reference: '',
      notes: ''
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      fromAccount: 'cash_in_hand',
      toAccount: 'cash_at_bank',
      amount: '',
      notes: ''
    });
  };

  const resetReconcileForm = () => {
    setReconcileForm({
      accountType: 'cash_in_hand',
      date: new Date().toISOString().split('T')[0],
      systemBalance: 0,
      physicalCount: '',
      difference: 0,
      notes: ''
    });
  };

  const getAccountName = (accountType) => {
    const account = accounts.find(a => a.accountType === accountType);
    return account ? account.accountName : accountType;
  };

  const getTransactionTypeColor = (type) => {
    return type === 'credit' ? 'success' : 'error';
  };

  const getTransactionTypeIcon = (type) => {
    return type === 'credit' ? <ArrowUpCircle size={18} /> : <ArrowDownCircle size={18} />;
  };

  const filteredTransactions = transactions.filter(t => {
    if (selectedAccount === 'all') return true;
    return t.accountType === selectedAccount;
  });

  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Cash & Bank Ledger
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowRightLeft size={20} />}
            onClick={() => setTransferDialog(true)}
          >
            Transfer
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => setTransactionDialog(true)}
          >
            Add Transaction
          </Button>
          <IconButton onClick={loadData} color="primary">
            <RefreshCw size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Account Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {accounts.map((account) => (
          <Grid item xs={12} sm={6} md={3} key={account.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography color="text.secondary" variant="body2">
                    {account.accountName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenReconcileDialog(account)}
                    title="Reconcile"
                  >
                    <RefreshCw size={16} />
                  </IconButton>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  LKR {(account.currentBalance || 0).toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">In</Typography>
                    <Typography variant="body2" color="success.main">
                      +{(account.totalCredits || 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Out</Typography>
                    <Typography variant="body2" color="error.main">
                      -{(account.totalDebits || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                {account.lastReconciled && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Last reconciled: {
                      typeof account.lastReconciled.toDate === 'function'
                        ? account.lastReconciled.toDate().toLocaleDateString()
                        : account.lastReconciled instanceof Date
                        ? account.lastReconciled.toLocaleDateString()
                        : new Date(account.lastReconciled).toLocaleDateString()
                    }
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* Total Balance Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Total Balance
              </Typography>
              <Typography variant="h4" sx={{ mb: 1 }}>
                LKR {totalBalance.toLocaleString()}
              </Typography>
              {cashFlowSummary && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Net Cash Flow
                  </Typography>
                  <Typography variant="body2">
                    {cashFlowSummary.netCashFlow >= 0 ? '+' : ''}
                    {cashFlowSummary.netCashFlow.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Account Filter */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Filter by Account
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="All Accounts"
            onClick={() => setSelectedAccount('all')}
            color={selectedAccount === 'all' ? 'primary' : 'default'}
          />
          {accounts.map((account) => (
            <Chip
              key={account.id}
              label={account.accountName}
              onClick={() => setSelectedAccount(account.accountType)}
              color={selectedAccount === account.accountType ? 'primary' : 'default'}
            />
          ))}
        </Box>
      </Paper>

      {/* Transactions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No transactions found. Add your first transaction to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTransactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>
                    {transaction.date && typeof transaction.date.toDate === 'function'
                      ? transaction.date.toDate().toLocaleDateString()
                      : transaction.date instanceof Date
                      ? transaction.date.toLocaleDateString()
                      : new Date(transaction.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getAccountName(transaction.accountType)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getTransactionTypeIcon(transaction.transactionType)}
                      label={transaction.transactionType === 'credit' ? 'Credit' : 'Debit'}
                      color={getTransactionTypeColor(transaction.transactionType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{transaction.description || '-'}</TableCell>
                  <TableCell>
                    <Chip label={transaction.referenceType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      fontWeight={600}
                      color={transaction.transactionType === 'credit' ? 'success.main' : 'error.main'}
                    >
                      {transaction.transactionType === 'credit' ? '+' : '-'}
                      LKR {transaction.amount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={500}>
                      LKR {transaction.balance.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {(transaction.referenceType === 'manual' || transaction.referenceType === 'adjustment') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTransaction(transaction)}
                        color="error"
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredTransactions.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Add Transaction Dialog */}
      <Dialog open={transactionDialog} onClose={() => setTransactionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Account"
              select
              value={transactionForm.accountType}
              onChange={(e) => setTransactionForm({ ...transactionForm, accountType: e.target.value })}
              fullWidth
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.accountType}>
                  {account.accountName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Transaction Type"
              select
              value={transactionForm.transactionType}
              onChange={(e) => setTransactionForm({ ...transactionForm, transactionType: e.target.value })}
              fullWidth
            >
              <MenuItem value="credit">Credit (Money In)</MenuItem>
              <MenuItem value="debit">Debit (Money Out)</MenuItem>
            </TextField>

            <TextField
              label="Amount"
              type="number"
              value={transactionForm.amount}
              onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={transactionForm.description}
              onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Payment Method"
              select
              value={transactionForm.paymentMethod}
              onChange={(e) => setTransactionForm({ ...transactionForm, paymentMethod: e.target.value })}
              fullWidth
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank Transfer</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="card">Card</MenuItem>
            </TextField>

            <TextField
              label="Reference Number"
              value={transactionForm.reference}
              onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
              fullWidth
            />

            <TextField
              label="Notes"
              value={transactionForm.notes}
              onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleAddTransaction} variant="contained" disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Funds</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="From Account"
              select
              value={transferForm.fromAccount}
              onChange={(e) => setTransferForm({ ...transferForm, fromAccount: e.target.value })}
              fullWidth
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.accountType}>
                  {account.accountName} (LKR {account.currentBalance.toLocaleString()})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="To Account"
              select
              value={transferForm.toAccount}
              onChange={(e) => setTransferForm({ ...transferForm, toAccount: e.target.value })}
              fullWidth
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.accountType}>
                  {account.accountName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Amount"
              type="number"
              value={transferForm.amount}
              onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Notes"
              value={transferForm.notes}
              onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained" disabled={submitting}>
            {submitting ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={reconcileDialog} onClose={() => setReconcileDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reconcile Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="info">
              Compare your physical count with the system balance and record any differences.
            </Alert>

            <TextField
              label="Account"
              value={getAccountName(reconcileForm.accountType)}
              disabled
              fullWidth
            />

            <TextField
              label="Date"
              type="date"
              value={reconcileForm.date}
              onChange={(e) => setReconcileForm({ ...reconcileForm, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="System Balance"
              value={`LKR ${reconcileForm.systemBalance.toLocaleString()}`}
              disabled
              fullWidth
            />

            <TextField
              label="Physical Count"
              type="number"
              value={reconcileForm.physicalCount}
              onChange={(e) => {
                const physical = e.target.value;
                const diff = calculateDifference(physical);
                setReconcileForm({ 
                  ...reconcileForm, 
                  physicalCount: physical,
                  difference: diff
                });
              }}
              required
              fullWidth
            />

            <TextField
              label="Difference"
              value={`LKR ${reconcileForm.difference.toLocaleString()}`}
              disabled
              fullWidth
              helperText={
                Math.abs(reconcileForm.difference) > 0.01
                  ? reconcileForm.difference > 0
                    ? 'Surplus - System will credit this amount'
                    : 'Shortage - System will debit this amount'
                  : 'No difference'
              }
            />

            <TextField
              label="Notes"
              value={reconcileForm.notes}
              onChange={(e) => setReconcileForm({ ...reconcileForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Explain any differences..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleReconcile} variant="contained" disabled={submitting}>
            {submitting ? 'Reconciling...' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Initialize Accounts Dialog */}
      <Dialog open={initDialog} onClose={() => {}} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>Initialize Cash & Bank Accounts</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="info">
              Enter your opening balances for each account. You can leave any account at 0 if not applicable.
            </Alert>

            <TextField
              label="Cash in Hand"
              type="number"
              value={initForm.cashInHand}
              onChange={(e) => setInitForm({ ...initForm, cashInHand: e.target.value })}
              fullWidth
              helperText="Counter cash, till money"
            />

            <TextField
              label="Petty Cash"
              type="number"
              value={initForm.pettyCash}
              onChange={(e) => setInitForm({ ...initForm, pettyCash: e.target.value })}
              fullWidth
              helperText="Small expenses float"
            />

            <TextField
              label="Cash at Bank"
              type="number"
              value={initForm.cashAtBank}
              onChange={(e) => setInitForm({ ...initForm, cashAtBank: e.target.value })}
              fullWidth
              helperText="Business bank account balance"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInitialize} variant="contained" disabled={submitting}>
            {submitting ? 'Initializing...' : 'Initialize Accounts'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Transaction?</DialogTitle>
        <DialogContent>
          <Typography>Delete this transaction? This will reverse the account balance. Cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashBankLedger;
